import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchVirtualAccountMe,
  createVirtualAccount,
  fetchFiatLimits,
  parseFiatDepositLimits,
  formatAedAmount,
  fetchBeneficiaries,
} from "../../../api/services/fiatService";
import FiatHoldBanner, { useFiatHold } from "../../../customComponents/FiatHoldBanner";
import { ProfileContext } from "../../../context/ProfileProvider";
import { isKycVerified as profileIsKycVerified, kycGateMessage } from "../../../utils/kycVerified";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { openKycVerification } from "../KycVara/openKycVerification";
import DepositFiatHistorySection from "./DepositFiatHistory";
import "./depositfiat.css";

const HOW_STEPS = [
  {
    n: "1",
    title: "Complete KYC",
    body: "The ideation process is a crucial phase in the design process where creative thinking and brainstorming",
    icon: "/images/deposit-fiat-img/kyc_vector.png",
  },
  {
    n: "2",
    title: "Create your AGCX IBAN",
    body: "Research is a critical component of the design process, helping designers understand the problem",
    icon: "/images/deposit-fiat-img/createiban_vector.png",
  },
  {
    n: "3",
    title: "Whitelist your account",
    body: "Designing a process involves several key steps to ensure clarity, efficiency, successful implementation",
    icon: "/images/deposit-fiat-img/whitelist_account_vector.png",
  },
  {
    n: "4",
    title: "Testing",
    body: "Testing is a crucial phase in the design process to ensure that the product or system meets the specified requirements",
    icon: "/images/deposit-fiat-img/testing_vector.png",
  },
];

function isKycVerified(userDetails) {
  return profileIsKycVerified(userDetails?.kycVerified);
}

function DfFiatPageSkeleton({ variant = "deposit" }) {
  const historyCols = variant === "withdraw" ? ["Date", "Amount", "Bank", "Status"] : ["Date", "Amount", "From", "Status"];
  return (
    <div className="deposit-fiat-wrapper df-sk-page" aria-busy="true" aria-label="Loading">
      <section
        className="df-hero-section"
        style={{ backgroundImage: "url('/images/deposit-fiat-img/fiat_herobg.png')" }}
      >
        <div className="container df-hero-container">
          <div className="df-hero-content">
            <span className="df-sk-glass df-sk-glass--tag" />
            <span className="df-sk-glass df-sk-glass--h1" />
            <span className="df-sk-glass df-sk-glass--h1s" />
            <span className="df-sk-glass df-sk-glass--lead" />
            <div className="df-features-list">
              {[0, 1, 2].map((i) => (
                <div className="df-feature-item" key={i}>
                  <span className="df-sk-glass df-sk-glass--icon" />
                  <div className="df-feature-text">
                    <span className="df-sk-glass df-sk-glass--feat-title" />
                    <span className="df-sk-glass df-sk-glass--feat-p" />
                  </div>
                </div>
              ))}
            </div>
            <div className="df-guide-card">
              <div className="df-guide-block">
                <span className="df-sk-glass df-sk-glass--feat-title" />
                <span className="df-sk-glass df-sk-glass--lead" />
              </div>
              <div className="df-guide-block">
                <span className="df-sk-glass df-sk-glass--feat-title" />
                <span className="df-sk-glass df-sk-glass--lead" />
              </div>
            </div>
          </div>

          <div className="df-hero-widget df-sk-widget">
            <div className="df-widget-header">
              <span className="df-sk-glass df-sk-glass--widget-title" />
              <span className="df-sk-glass df-sk-glass--pill" />
            </div>
            <div className="df-va-row">
              <span className="df-sk-glass df-sk-glass--label" />
              <span className="df-sk-glass df-sk-glass--line df-sk-w70" />
            </div>
            <div className="df-va-row">
              <span className="df-sk-glass df-sk-glass--label" />
              <span className="df-sk-glass df-sk-glass--line df-sk-w60" />
            </div>
            <div className="df-va-row">
              <span className="df-sk-glass df-sk-glass--label" />
              <span className="df-sk-glass df-sk-glass--line df-sk-w70" />
            </div>
            <div className="df-va-row">
              <span className="df-sk-glass df-sk-glass--label" />
              <span className="df-sk-glass df-sk-glass--line df-sk-w90" />
            </div>
            <div className="df-va-row">
              <span className="df-sk-glass df-sk-glass--label" />
              <span className="df-sk-glass df-sk-glass--line df-sk-w50" />
            </div>
          </div>
        </div>
      </section>

      <section className="df-why-section">
        <div className="container">
          <span className="df-sk-glass df-sk-glass--section-title" />
          <div className="df-why-grid">
            {[0, 1, 2, 3].map((i) => (
              <div className="df-why-card df-sk-why-card" key={i}>
                <span className="df-sk-glass df-sk-glass--why-icon" />
                <span className="df-sk-glass df-sk-glass--line" />
                <span className="df-sk-glass df-sk-glass--line df-sk-w80" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="df-deposit-history-section">
        <div className="container">
          <div className="df-deposit-history-top">
            <span className="df-sk-glass df-sk-glass--hist-title" />
          </div>
          <div className="df-deposit-history-card">
            <div className="df-sk-table-wrap">
              <div className="df-sk-table-head">
                {historyCols.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </div>
              {Array.from({ length: 6 }, (_, i) => (
                <div className="df-sk-row" key={i}>
                  <div className="df-sk-coin">
                    <span className="df-sk-circle df-sk-glass" />
                    <div className="df-sk-coin-txt">
                      <span className="df-sk-glass df-sk-bone--xs" />
                      <span className="df-sk-glass df-sk-bone--xxs" />
                    </div>
                  </div>
                  <span className="df-sk-glass df-sk-bone--cell" />
                  <span className="df-sk-glass df-sk-bone--cell" />
                  <span className="df-sk-glass df-sk-bone--cell df-sk-w50" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function formatCreatedDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function vaStatusChip(status) {
  const raw = String(status || "ACTIVE").toUpperCase();
  if (raw === "ACTIVE") return { text: "Active", pending: false };
  const text = raw.charAt(0) + raw.slice(1).toLowerCase();
  return { text: text || "Pending", pending: true };
}

function DepositLimitsList({ limits }) {
  if (!limits) return null;
  return (
    <div className="df-limit-list">
      <div className="df-limit-list__title">Deposit limits</div>
      {limits.minPerTx != null ? (
        <div className="df-limit-row">
          <span>Minimum</span>
          <strong>{formatAedAmount(limits.minPerTx)} AED</strong>
        </div>
      ) : null}
      {/* {limits.maxPerTx != null ? (
        <div className="df-limit-row">
          <span>Max per transfer</span>
          <strong>{formatAedAmount(limits.maxPerTx)} AED</strong>
        </div>
      ) : null}
      {limits.dailyRemaining != null ? (
        <div className="df-limit-row">
          <span>Daily remaining</span>
          <strong>{formatAedAmount(limits.dailyRemaining)} AED</strong>
        </div>
      ) : null}
      {limits.monthlyRemaining != null ? (
        <div className="df-limit-row">
          <span>Monthly remaining</span>
          <strong>{formatAedAmount(limits.monthlyRemaining)} AED</strong>
        </div>
      ) : null} */}
      {limits.freeDepositsRemaining != null ? (
        <div className="df-limit-row df-limit-row--sub">
          <span>Free transfers per 24 hours</span>
          <strong>{limits.freeDepositsRemaining}</strong>
        </div>
      ) : null}
      {/* {limits.nextDepositFeeAed != null ? (
        <div className="df-limit-row df-limit-row--sub">
          <span>Next deposit fee</span>
          <strong>{formatAedAmount(limits.nextDepositFeeAed)} AED</strong>
        </div>
      ) : null} */}
    </div>
  );
}

function DepositFeeAndHowToCard({ limits }) {
  const minN = Number(limits?.minPerTx != null ? limits.minPerTx : 100);
  const feeN = Number.parseFloat(String(limits?.nextDepositFeeAed ?? "10"));
  const fee = Number.isFinite(feeN) ? feeN : 10;
  const minLabel = formatAedAmount(Number.isFinite(minN) ? minN : 100);
  const feeLabel = formatAedAmount(fee);
  const netLabel = formatAedAmount(Math.max(0, (Number.isFinite(minN) ? minN : 100) - fee));
  const freeLeft = Number(limits?.freeDepositsRemaining);
  const feeIsFree = Number.isFinite(freeLeft) && freeLeft > 0;

  return (
    <aside className="df-guide-card" aria-label="Deposit fees and instructions">
      <div className="df-guide-block">
        <div className="df-guide-block__head">
          {/* <span className="df-guide-block__icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" />
            </svg>
          </span> */}
          <span>Fee details</span>
        </div>
        <div className="df-fee-stats">
          {/* <div className="df-fee-stat">
            <span>This deposit</span>
            <strong className={feeIsFree ? "df-fee-stat__ok" : ""}>
              {feeIsFree ? "Free" : `${feeLabel} AED`}
            </strong>
          </div> */}
          <div className="df-fee-stat">
            <span>Send at least</span>
            <strong>{minLabel} AED</strong>
          </div>
          <div className="df-fee-stat">
            <span>You receive</span>
            <strong>{feeIsFree ? `${minLabel} AED` : `${netLabel} AED`}</strong>
          </div>
        </div>
        <p className="df-guide-note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          {feeIsFree
            ? `${freeLeft} free deposit${freeLeft === 1 ? "" : "s"} left today. Fee is taken from the bank transfer.`
            : `Example if you send the minimum. Fee is taken from the transfer automatically — do not add it in the app. Below ${minLabel} AED is held for refund, not credited.`}
        </p>
      </div>
      <div className="df-guide-block">
        <div className="df-guide-block__head">
          {/* <span className="df-guide-block__icon" aria-hidden="true">
        
          </span> */}
          <span>How to deposit</span>
        </div>

        <div className="dividerbdr"></div>

        <ol className="df-how-steps">
          <li>
            <span>Copy the <strong>AGCX IBAN</strong> and <strong>account name</strong> from the bank card.</span>
          </li>
          <li>
            <span>Transfer at least <strong>{minLabel} AED</strong> from your UAE bank.</span>
          </li>
          <li><span>Wait for AED to credit in your spot wallet.</span></li>
        </ol>
      </div>
    </aside>
  );
}

function vaPayInIban(account) {
  const full = String(account?.iban || "").replace(/\s+/g, "").trim();
  if (full) return { text: full, copy: full, complete: true };
  const last4 = String(account?.iban_last4 || "").trim();
  if (last4) return { text: `•••• ${last4}`, copy: "", complete: false };
  return { text: "—", copy: "", complete: false };
}

function maskWithAsterisks(val, fallbackLast4) {
  if (!val || val === "—") {
    if (fallbackLast4) {
      const l4 = String(fallbackLast4).trim();
      return l4 ? `********${l4.slice(-4)}` : "—";
    }
    return "—";
  }
  const clean = String(val).replace(/\s+/g, "").trim();
  const last4 = clean.includes("•")
    ? (fallbackLast4 || clean.replace(/\D/g, "").slice(-4))
    : clean.slice(-4);
  if (!last4) return "—";
  return `********${last4}`;
}

async function copyText(value) {
  const text = String(value || "");
  if (!text) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

function DepositFiatKycModal({ open, onClose, reason = "required", kycVerified }) {
  const navigate = useNavigate();
  const incomplete = reason === "incomplete";

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="df-kyc-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="df-kyc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="df-kyc-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="df-kyc-modal-close" aria-label="Close" onClick={onClose}>
          <i className="ri-close-line" aria-hidden="true" />
        </button>
        <div className="df-kyc-modal-art">
          <img className="dark_img" src="/images/security/account_restrictions.svg" alt="" />
          <img className="light_img" src="/images/security/security_risk_vector_light.svg" alt="" />
        </div>
        <h2 id="df-kyc-modal-title" className="df-kyc-modal-title">
          {incomplete ? "KYC details incomplete" : "Identity verification required"}
        </h2>
        <p className="df-kyc-modal-text">
          {incomplete
            ? "Your verified profile is missing details the bank needs (name, date of birth, document number, nationality, or expiry). Open KYC to update your documents — creating another virtual account will not fix this."
            : kycGateMessage(kycVerified)}
        </p>
        <div className="df-kyc-modal-actions">
          {/* Missing bank details on an approved profile still go to the old KYC page; not verified → KYC modal. */}
          <button
            type="button"
            className="df-kyc-modal-confirm"
            onClick={() => {
              if (incomplete) {
                navigate("/user_profile/kyc");
                return;
              }
              onClose();
              openKycVerification();
            }}
          >
            {incomplete ? "Update KYC" : "Verify now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DepositFiatCreateAccountCard({
  loading,
  creating,
  error,
  onCreateAccount,
  onRetry,
  held,
  loadFailed,
  depositLimits,
}) {
  let primaryLabel = "Create bank account";
  if (loading) primaryLabel = "Checking account…";
  else if (creating) primaryLabel = "Creating bank account…";
  else if (held) primaryLabel = "Fiat is paused";
  else if (loadFailed) primaryLabel = "Try again";

  const disabled = loading || creating || held;
  const onClick = loadFailed && !held ? onRetry : onCreateAccount;

  return (
    <div className="df-hero-widget">
      <div className="df-widget-header">
        <h3>Deposit Fiat</h3>
        <Link to="/asset_managemnet/withdraw_fiat" className="df-withdrawal-btn">
          Fiat Withdrawal
        </Link>
      </div>

      {error ? (
        <div className="df-alert df-alert--error" style={{ marginTop: 12 }}>
          {error}
        </div>
      ) : null}

      <div className="df-payment-method">
        <div className="paybl_methoddl">

          <div className="df-method-icon">
            <img src="/images/deposit-fiat-img/kyc_icon_cmt.png" alt="Bank" />
          </div>
          <div className="df-method-details">
            <div className="df-method-title-row">
              <div className="df-method-title">
                Your KYC is not verified. Complete the  KYC first.
              </div>
            </div>
            <div className="kyc_complete"><a href="#">Complete KYC <i class="ri-arrow-right-up-line"></i></a></div>
          </div>
        </div>
      </div>

      <div className="df-input-group">
        <span className="df-input-label">Currency</span>
        <div className="df-input-box">
          <img src="/images/deposit-fiat-img/aed_currency.png" alt="AED" />
          <input type="text" readOnly value="AED" aria-readonly="true" />
          <div className="df-currency-selector">
            {/* <div className="df-currency-icon">A</div> */}
            AED
          </div>
        </div>
      </div>

      <span className="df-input-label">Payment Method</span>
      <div className="df-payment-method">
        <div className="paybl_methoddl">

          <div className="df-method-icon">
            <img src="/images/deposit-fiat-img/bank_icon.svg" alt="Bank" />
          </div>
          <div className="df-method-details">
            <div className="df-method-title-row">
              <div className="df-method-title">
                Bank Transfer (UAE)
                <span className="df-recommended-tag">Recommended</span>
              </div>
              <div className="checkiconbnk">
                <i class="ri-check-fill"></i>
              </div>
            </div>
            <div className="df-method-fee">Your Personal AGCX Virtual IBAN</div>
          </div>
        </div>

        <div className="df-method-desc">
          Create your Virtual IBAN that will be linked to your verified identity. This VIBAN can be used to deposit
          funds from your verified bank account to your AGCX account.
        </div>
      </div>

      <DepositLimitsList limits={depositLimits} />

      <button type="button" className="df-submit-btn" onClick={onClick} disabled={disabled}>
        {primaryLabel} <i class="ri-arrow-right-line"></i>
      </button>
    </div>
  );
}

function DepositFiatVirtualAccountCard({ account, depositLimits, validateBeneficiary }) {
  const [copiedField, setCopiedField] = useState("");
  const [showAccountNo, setShowAccountNo] = useState(false);
  const [showIban, setShowIban] = useState(false);

  const payIn = vaPayInIban(account);
  const chip = vaStatusChip(account?.status);
  const accountNo = String(account?.account_id || "").trim();

  const copyField = useCallback(async (value, field, successMsg, failMsg) => {
    if (!value) return;
    const ok = await copyText(value);
    if (!ok) {
      alertErrorMessage(failMsg);
      return;
    }
    setCopiedField(field);
    alertSuccessMessage(successMsg);
    window.setTimeout(() => {
      setCopiedField((cur) => (cur === field ? "" : cur));
    }, 2000);
  }, []);

  const onCopyIban = useCallback(async () => {
    if (validateBeneficiary) {
      const valid = await validateBeneficiary();
      if (!valid) return;
    }
    if (!payIn.copy) return;
    return copyField(
      payIn.copy,
      "iban",
      "IBAN copied",
      "Could not copy IBAN. Select it and copy manually."
    );
  }, [validateBeneficiary, copyField, payIn.copy]);

  const onCopyAccountNo = useCallback(async () => {
    if (validateBeneficiary) {
      const valid = await validateBeneficiary();
      if (!valid) return;
    }
    if (!accountNo) return;
    return copyField(
      accountNo,
      "account",
      "Account no copied",
      "Could not copy account no. Select it and copy manually."
    );
  }, [validateBeneficiary, copyField, accountNo]);

  const onToggleShowAccountNo = useCallback(async () => {
    if (!showAccountNo && validateBeneficiary) {
      const valid = await validateBeneficiary();
      if (!valid) return;
    }
    setShowAccountNo((prev) => !prev);
  }, [showAccountNo, validateBeneficiary]);

  const onToggleShowIban = useCallback(async () => {
    if (!showIban && validateBeneficiary) {
      const valid = await validateBeneficiary();
      if (!valid) return;
    }
    setShowIban((prev) => !prev);
  }, [showIban, validateBeneficiary]);

  const displayAccountNo = accountNo
    ? showAccountNo
      ? accountNo
      : maskWithAsterisks(accountNo)
    : "—";

  const displayIban =
    payIn.text !== "—"
      ? showIban
        ? payIn.text
        : maskWithAsterisks(payIn.copy || payIn.text, account?.iban_last4)
      : "—";

  return (
    <div className="df-hero-widget df-va-card">
      <div className="df-widget-header">
        <div className="d-flex gap-2 align-items-center">
          <h3 className="mb-0">Your AGCX IBAN</h3>
          <div className="df-va-status-row">
            <span className={`df-va-status${chip.pending ? " df-va-status--pending" : ""}`}>
              {chip.text}
            </span>
            {/* <span className="df-va-currency">{account?.currency || "AED"}</span> */}
          </div>
        </div>
        <Link to="/asset_managemnet/withdraw_fiat" className="df-withdrawal-btn">
          Fiat Withdrawal
        </Link>
      </div>

      <div className="df-va-row">
        <span className="df-va-label">Account name</span>
        <span className="df-va-value">{account?.account_name || "—"}</span>
      </div>
      <div className="df-va-row">
        <span className="df-va-label">Bank</span>
        <span className="df-va-value">AGCX Bank</span>
      </div>
      <div className="df-va-row">
        <span className="df-va-label">Account no</span>
        <span className="df-va-value df-va-value--iban">
          <span className="df-va-num-text">{displayAccountNo}</span>
          {accountNo ? (
            <div className="df-va-actions">
              <button
                type="button"
                className="df-va-copy"
                onClick={onToggleShowAccountNo}
                title={showAccountNo ? "Hide account no" : "Show account no"}
                aria-label={showAccountNo ? "Hide account no" : "Show account no"}
              >
                <i className={showAccountNo ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
              <button
                type="button"
                className="df-va-copy"
                onClick={onCopyAccountNo}
                title="Copy account no"
                aria-label="Copy account no"
              >
                {copiedField === "account" ? "Copied" : <i className="ri-file-copy-line"></i>}
              </button>
            </div>
          ) : null}
        </span>
      </div>
      <div className="df-va-row df-va-row--iban">
        <span className="df-va-label">IBAN</span>
        <span className="df-va-value df-va-value--iban">
          <span className="df-va-num-text">{displayIban}</span>
          {payIn.copy ? (
            <div className="df-va-actions">
              <button
                type="button"
                className="df-va-copy"
                onClick={onToggleShowIban}
                title={showIban ? "Hide IBAN" : "Show IBAN"}
                aria-label={showIban ? "Hide IBAN" : "Show IBAN"}
              >
                <i className={showIban ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
              <button
                type="button"
                className="df-va-copy"
                onClick={onCopyIban}
                title="Copy IBAN"
                aria-label="Copy IBAN"
              >
                {copiedField === "iban" ? "Copied" : <i className="ri-file-copy-line"></i>}
              </button>
            </div>
          ) : null}
        </span>
      </div>
      {!payIn.complete && payIn.text !== "—" ? (
        <p className="df-va-iban-missing">
          Full IBAN is not available. Use last4 above and contact support if your bank needs the complete number.
        </p>
      ) : null}
      <div className="df-va-row">
        <span className="df-va-label">Created</span>
        <span className="df-va-value">{formatCreatedDate(account?.created_at)}</span>
      </div>

      <DepositLimitsList limits={depositLimits} />

      <div className="df-payment-method beneficiarynote" role="note">
        <span className="df-va-beneficiary-note__icon" aria-hidden="true">!</span>
        <p>
          Add your withdrawals account before you deposit. It must be registered in your name.
          Deposits from third-party or unauthorized accounts may be rejected and refunded.
          Use only your own bank account.
        </p>
      </div>
      <Link to="/asset_managemnet/withdraw_fiat?add=1" className="df-submit-btn">
        {/* <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <line x1="19" y1="8" x2="19" y2="14" />
          <line x1="22" y1="11" x2="16" y2="11" />
        </svg> */}
        Add withdrawal account <i class="ri-arrow-right-line"></i>
      </Link>
    </div>
  );
}

export default function DepositFiatPage({ userDetails: userDetailsProp }) {
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = userDetailsProp || profileCtx.userDetails;
  const profileHydrated = profileCtx.profileHydrated !== false;
  const hold = useFiatHold();

  const [vaLoading, setVaLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [creating, setCreating] = useState(false);
  const [widgetError, setWidgetError] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [kycReason, setKycReason] = useState("required");
  const [depositLimits, setDepositLimits] = useState(null);
  const beneficiariesPromiseRef = useRef(null);

  const loadDepositLimits = useCallback(async () => {
    const res = await fetchFiatLimits();
    if (res.ok) {
      setDepositLimits(parseFiatDepositLimits(res.limits));
    }
  }, []);

  const loadBeneficiaries = useCallback(async () => {
    try {
      const p = fetchBeneficiaries();
      beneficiariesPromiseRef.current = p;
      const res = await p;
      if (res?.ok) {
        setBeneficiaries(res.items || []);
      } else {
        setBeneficiaries([]);
      }
      return res;
    } catch {
      setBeneficiaries([]);
      return null;
    } finally {
      beneficiariesPromiseRef.current = null;
    }
  }, []);

  const validateBeneficiary = useCallback(async () => {
    let currentBeneficiaries = beneficiaries;
    if (beneficiariesPromiseRef.current) {
      try {
        const res = await beneficiariesPromiseRef.current;
        if (res?.ok && Array.isArray(res.items)) {
          currentBeneficiaries = res.items;
        }
      } catch {
        /* ignore */
      }
    }
    if (!currentBeneficiaries || currentBeneficiaries.length === 0) {
      try {
        const freshRes = await fetchBeneficiaries();
        if (freshRes?.ok && Array.isArray(freshRes.items) && freshRes.items.length > 0) {
          setBeneficiaries(freshRes.items);
          return true;
        }
      } catch {
        /* ignore */
      }
      alertErrorMessage("Please add beneficiary account before deposit.");
      return false;
    }
    return true;
  }, [beneficiaries]);

  const checkVirtualAccount = useCallback(async () => {
    setVaLoading(true);
    setWidgetError("");
    setLoadFailed(false);
    const res = await fetchVirtualAccountMe();
    if (res.ok && res.account) {
      setAccount(res.account);
    } else if (res.notFound) {
      setAccount(null);
    } else {
      setAccount(null);
      setLoadFailed(true);
      setWidgetError(res.error?.message || "Could not check fiat account status.");
    }
    setVaLoading(false);
  }, []);

  useEffect(() => {
    checkVirtualAccount();
    loadDepositLimits();
    loadBeneficiaries();
  }, [checkVirtualAccount, loadDepositLimits, loadBeneficiaries]);

  useEffect(() => {
    const onFocus = () => {
      loadDepositLimits();
      loadBeneficiaries();
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        loadDepositLimits();
        loadBeneficiaries();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loadDepositLimits, loadBeneficiaries]);

  const closeKycModal = useCallback(() => setKycModalOpen(false), []);

  const openKycModal = useCallback((reason) => {
    setKycReason(reason === "incomplete" ? "incomplete" : "required");
    setKycModalOpen(true);
  }, []);

  const handleCreateAccount = useCallback(async () => {
    if (hold.held || creating) return;

    if (profileHydrated && !isKycVerified(userDetails)) {
      openKycModal("required");
      return;
    }

    setCreating(true);
    setWidgetError("");
    const res = await createVirtualAccount();
    setCreating(false);

    if (res.ok && res.account) {
      setAccount(res.account);
      setLoadFailed(false);
      alertSuccessMessage(res.created ? "Bank account created." : "Your virtual account is ready.");
      return;
    }

    const code = res.error?.code;
    if (code === "KYC_REQUIRED") {
      openKycModal("required");
      return;
    }
    if (code === "KYC_PROFILE_INCOMPLETE") {
      openKycModal("incomplete");
      return;
    }
    setWidgetError(res.error?.message || "Could not create virtual account. Please try again.");
  }, [creating, hold.held, openKycModal, profileHydrated, userDetails]);

  return (
    <>
      <div className="dashboard_right fiatmobile">
        {vaLoading ? (
          <DfFiatPageSkeleton variant="deposit" />
        ) : (
          <div className="deposit-fiat-wrapper">
            <section className="df-hero-section" style={{ backgroundImage: "url('/images/deposit-fiat-img/fiat_herobg.png')" }}>
              <div className="container df-hero-container">
                <div className="df-hero-content">
                  <FiatHoldBanner />
                  {/* <div className="df-gateway-tag">FIAT GATEWAY</div> */}
                  <h1>
                    Deposit Fiat.
                    Buy Crypto.
                    <br /><span>Seamlessly.</span>
                  </h1>
                  <p>Add funds to your AGXC account using bank transfer and start trading in minutes. Fast, secure and hassle-free.</p>

                  <div className="df-features-list">
                    <div className="df-feature-item">
                      <div className="df-feature-icon">
                        <img className="light_img" src="/images/deposit-fiat-img/fiat_bnr_icon.png" alt="" />
                        <img className="dark_img" src="/images/deposit-fiat-img/fiat_bnr_icon_light.png" alt="" />
                      </div>
                      <div className="df-feature-text">
                        <h4>Bank-level security</h4>
                        <p>Protected with advanced security systems.</p>
                      </div>
                    </div>
                    <div className="df-feature-item">
                      <div className="df-feature-icon">
                        <img className="light_img" src="/images/deposit-fiat-img/fiat_bnr_icon2.png" alt="" />
                        <img className="dark_img" src="/images/deposit-fiat-img/fiat_bnr_icon2_light.png" alt="" />
                      </div>
                      <div className="df-feature-text">
                        <h4>Fast processing</h4>
                        <p>Start trading as soon as Spot credits.</p>
                      </div>
                    </div>
                    <div className="df-feature-item">
                      <div className="df-feature-icon">
                        <img className="light_img" src="/images/deposit-fiat-img/fiat_bnr_icon3.png" alt="" />
                        <img className="dark_img" src="/images/deposit-fiat-img/fiat_bnr_icon3_light.png" alt="" />
                      </div>
                      <div className="df-feature-text">
                        <h4>UAE AED rail</h4>
                        <p>Dedicated AGCX virtual IBAN.</p>
                      </div>
                    </div>
                  </div>

                  <DepositFeeAndHowToCard limits={depositLimits} />
                </div>

                {account ? (
                  <DepositFiatVirtualAccountCard
                    account={account}
                    depositLimits={depositLimits}
                    validateBeneficiary={validateBeneficiary}
                  />
                ) : (
                  <DepositFiatCreateAccountCard
                    loading={vaLoading}
                    creating={creating}
                    error={widgetError}
                    onCreateAccount={handleCreateAccount}
                    onRetry={checkVirtualAccount}
                    held={hold.held}
                    loadFailed={loadFailed}
                    depositLimits={depositLimits}
                  />
                )}
              </div>
            </section>

            <section className="df-why-section" aria-labelledby="df-how-title">
              <div className="container">
                <h2 id="df-how-title" className="df-section-title">
                  How to deposit <span>Fiat</span>?
                </h2>
                <div className="df-why-grid">
                  {HOW_STEPS.map((step) => (
                    <div className="df-why-card" key={step.n}>

                      <img className="afterarrow" src="/images/deposit-fiat-img/boxafter_arrow.png" alt="" />
                      <div className="tophd_how d-flex align-items-center">
                        <div className="df-why-icon">
                          <img src={step.icon} alt="" />
                        </div>
                        <h4>{step.title}
                        </h4>
                      </div>
                      <p>{step.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <DepositFiatHistorySection />
          </div>
        )}
      </div>

      <DepositFiatKycModal open={kycModalOpen} onClose={closeKycModal} reason={kycReason} kycVerified={userDetails?.kycVerified} />
    </>
  );
}
